import * as i18n from "@solid-primitives/i18n"
import { createEffect, createMemo, createResource } from "solid-js"
import { createStore } from "solid-js/store"
import { createSimpleContext } from "@opencode-ai/ui/context"
import { Persist, persisted } from "@/utils/persist"
import { dict as en } from "@/i18n/en"
import { dict as uiEn } from "@opencode-ai/ui/i18n/en"

// English-only build (Wallarm agentic-ui). Other locales were removed; the
// public API (Locale, useLanguage, LanguageProvider, loadLocaleDict,
// normalizeLocale) is kept so consumers are unaffected.
export type Locale = "en"

type RawDictionary = typeof en & typeof uiEn
type Dictionary = i18n.Flatten<RawDictionary>

function cookie(locale: Locale) {
  return `oc_locale=${encodeURIComponent(locale)}; Path=/; Max-Age=31536000; SameSite=Lax`
}

const LOCALES: readonly Locale[] = ["en"]
const INTL: Record<Locale, string> = { en: "en" }
const LABEL_KEY: Record<Locale, keyof Dictionary> = { en: "language.en" }

const base = i18n.flatten({ ...en, ...uiEn })

function loadDict(_locale: Locale) {
  return Promise.resolve(base)
}

export function loadLocaleDict(_locale: Locale) {
  return loadDict("en").then(() => undefined)
}

export function normalizeLocale(_value: string): Locale {
  return "en"
}

export const { use: useLanguage, provider: LanguageProvider } = createSimpleContext({
  name: "Language",
  gate: false,
  init: (_props: { locale?: Locale }) => {
    const [, setStore, , ready] = persisted(
      Persist.global("language", ["language.v1"]),
      createStore({ locale: "en" as Locale }),
    )

    const locale = createMemo<Locale>(() => "en")
    const intl = createMemo(() => INTL[locale()])

    const [dict] = createResource(locale, loadDict, {
      initialValue: base,
    })

    const t = i18n.translator(() => dict() ?? base, i18n.resolveTemplate) as (
      key: keyof Dictionary,
      params?: Record<string, string | number | boolean>,
    ) => string

    const label = (value: Locale) => t(LABEL_KEY[value])

    createEffect(() => {
      if (typeof document !== "object") return
      document.documentElement.lang = locale()
      document.cookie = cookie(locale())
    })

    return {
      ready,
      locale,
      intl,
      locales: LOCALES,
      label,
      t,
      setLocale(_next: Locale) {
        setStore("locale", "en")
      },
    }
  },
})
